import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import type { EventOrigin } from "apis/procore";
import { useAppDispatch, useAppSelector, useAppStore } from "app/redux-store-interactions";
import { useDispatchHidden, useHidden } from "contexts/hidden";
import { useDispatchHighlighted, useHighlighted } from "contexts/highlighted";
import { useDispatchSelectionBasket } from "contexts/selectionBasket";
import { useFlyToSelected } from "features/flyToSelected/useFlyToSelected";
import {
    selectDefaultVisibility,
    selectMainObject,
    selectMainObjects,
    selectPreIsolationVisibility,
} from "features/render";

import { useIsolationVisibility } from "../objectTree/hooks/useIsolationVisibility";
import { createBulkActions } from "../objectTree/utils/bulkActions";
import type { ContextMenuState } from "./ContextMenuPopup";
import { ContextMenuPopup } from "./ContextMenuPopup";

interface UseBulkActionsContextMenuOptions {
    origin?: EventOrigin;
}

/**
 * Shared hook for bulk-action context menus.
 *
 * Manages the menu state (position + actions), keeps the actions fresh while
 * the menu is open (e.g. when async selection completes after the menu is
 * already rendered), and exposes the `openContextMenu` / `closeContextMenu`
 * callbacks plus a ready-to-render `<ContextMenuPopup>` component.
 */
export function useBulkActionsContextMenu({ origin }: UseBulkActionsContextMenuOptions = {}) {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const store = useAppStore();
    const mainObject = useAppSelector(selectMainObject);
    const defaultVisibility = useAppSelector(selectDefaultVisibility);
    const preIsolationVisibility = useAppSelector(selectPreIsolationVisibility);
    const highlightedSet = useHighlighted();
    const mainObjects = useAppSelector(selectMainObjects);
    const dispatchHighlighted = useDispatchHighlighted();
    const dispatchHidden = useDispatchHidden();
    const hidden = useHidden();
    const isolationVisibility = useIsolationVisibility();
    const dispatchSelectionBasket = useDispatchSelectionBasket();
    const { flyToSelected } = useFlyToSelected();

    const [contextMenu, setContextMenu] = useState<ContextMenuState | null>(null);

    const closeContextMenu = useCallback(() => {
        setContextMenu(null);
    }, []);

    // V1 tree selection never populates mainObjects; fall back to the full geometry
    // set so count, disabled checks, and labels all reflect the actual selection.
    const effectiveSelection = mainObjects.length > 0 ? mainObjects : highlightedSet.idArr;

    const buildActions = useCallback(
        (onActionComplete: () => void) =>
            createBulkActions(effectiveSelection.length, effectiveSelection, {
                t,
                dispatch,
                store,
                dispatchHighlighted,
                dispatchHidden,
                dispatchSelectionBasket,
                defaultVisibility,
                preIsolationVisibility,
                flyToSelected,
                currentMainObject: mainObject,
                onActionComplete,
                hiddenIds: hidden.ids,
                lastHiddenIds: hidden.lastHiddenIds,
                isolationVisibility,
                geometryIds: highlightedSet.idArr,
                origin,
            }),
        [
            effectiveSelection,
            highlightedSet.idArr,
            t,
            dispatch,
            store,
            dispatchHighlighted,
            dispatchHidden,
            dispatchSelectionBasket,
            defaultVisibility,
            preIsolationVisibility,
            flyToSelected,
            mainObject,
            hidden.ids,
            hidden.lastHiddenIds,
            isolationVisibility,
            origin,
        ],
    );

    const openContextMenu = useCallback(
        (mouseX: number, mouseY: number) => {
            const actions = buildActions(closeContextMenu);
            setContextMenu({ mouseX, mouseY, actions });
        },
        [buildActions, closeContextMenu],
    );

    // Update context menu actions when selection changes while menu is open.
    // This handles the case where right-click triggers async selection that completes
    // after the menu is already displayed, ensuring menu items are enabled correctly.
    useEffect(() => {
        setContextMenu((openMenu) => {
            if (!openMenu) {
                return null;
            }

            const updatedActions = buildActions(closeContextMenu);
            return { ...openMenu, actions: updatedActions };
        });
    }, [buildActions, closeContextMenu]);

    const contextMenuNode = useMemo(
        () => <ContextMenuPopup contextMenu={contextMenu} onClose={closeContextMenu} />,
        [contextMenu, closeContextMenu],
    );

    return {
        contextMenu,
        openContextMenu,
        closeContextMenu,
        buildActions,
        contextMenuNode,
    };
}
